/**
 * NMS Clearance Store Component
 * Size-first clearance carousel on top of nms-product-card. Picking a size:
 *  - hides styles with no color in that size, and swatches of colors without it
 *  - switches a card to an in-stock color when its current color lacks the size
 *  - ticks that size on each card, so the card's own Add to Cart adds it directly
 * The cards' size boxes are hidden by CSS; this component drives them.
 */

class NMSClearanceStore extends window.NMSBaseClass {
  #selectedSize = '';
  #itemData = new WeakMap();

  constructor(element) {
    super();

    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;
    this.lowStock = Number(element.dataset.lowStock) || 0;
    this.stockTemplate = element.dataset.stockTemplate || '';

    window.neemans = window.neemans || {};
    window.neemans.clearanceStore = window.neemans.clearanceStore || {};
    window.neemans.clearanceStore[this.sectionId] = this;
  }

  init() {
    this.items = Array.from(this.element.querySelectorAll('[data-clearance-item]'));
    this.carousel = this.element.querySelector('[data-carousel]');
    this.emptyMessage = this.element.querySelector('[data-empty-message]');
    this.#selectedSize = this.element.dataset.selectedSize || '';

    this.element.addEventListener('change', this.#onSizeChange);
    document.addEventListener('nms:color-switched', this.#onColorSwitched);

    if (this.#selectedSize) this.applySize(this.#selectedSize, { scroll: false });
  }

  destroy() {
    super.destroy();
    this.element.removeEventListener('change', this.#onSizeChange);
    document.removeEventListener('nms:color-switched', this.#onColorSwitched);
    if (window.neemans.clearanceStore?.[this.sectionId]) {
      delete window.neemans.clearanceStore[this.sectionId];
    }
  }

  get selectedSize() {
    return this.#selectedSize;
  }

  #onSizeChange = (event) => {
    if (!event.target.matches('[data-size-input]')) return;
    this.applySize(event.target.value);
  };

  // Fires for manual swatch clicks and for switches made by applySize
  #onColorSwitched = (event) => {
    const card = event.detail?.component?.element;
    const item = card?.closest('[data-clearance-item]');
    if (!item || !this.element.contains(item)) return;
    this.#selectCardSize(item);
  };

  /**
   * Show only styles/colors with stock in the given size and preselect it on every card
   * @param {string} size - Size number, e.g. '7'
   * @param {{ scroll?: boolean }} [options]
   */
  applySize(size, { scroll = true } = {}) {
    this.#selectedSize = size;
    this.element.dataset.selectedSize = size;
    let visibleItems = 0;

    for (const item of this.items) {
      const { colors } = this.#getItemData(item);
      const inStock = Object.keys(colors).filter(handle => colors[handle].includes(` ${size} `));

      item.hidden = inStock.length === 0;
      if (item.hidden) continue;
      visibleItems++;

      const swatches = Array.from(item.querySelectorAll('[data-ref="colorSwatch"]'));
      for (const swatch of swatches) {
        swatch.hidden = !inStock.includes(swatch.dataset.productHandle);
      }

      const activeHandle = this.#activeHandle(item);
      if (inStock.includes(activeHandle)) {
        this.#selectCardSize(item);
        continue;
      }

      // Current color lacks the size: switch to an in-stock color; size is applied on nms:color-switched
      const target = swatches.find(swatch => !swatch.hidden);
      if (target) {
        target.click();
      } else {
        this.#selectCardSize(item);
      }
    }

    if (this.emptyMessage) {
      this.emptyMessage.hidden = visibleItems > 0;
      this.emptyMessage.textContent = this.emptyMessage.dataset.template.replace('[size]', size);
    }

    this.#updateViewAll(size);

    if (scroll) {
      this.carousel?.scrollTo({ left: 0, behavior: 'instant' });
      this.carousel?.dispatchEvent(new Event('scroll'));
    }
  }

  #activeHandle(item) {
    const active = item.querySelector('[data-ref="colorSwatch"].nms-product-card__color-swatch--active');
    return active?.dataset.productHandle || item.dataset.productHandle;
  }

  // Tick the card's (hidden) size box so nms-product-card selects that variant itself
  #selectCardSize(item) {
    const size = this.#selectedSize;
    const input = Array.from(item.querySelectorAll('.nms-product-card__size-input')).find(
      el => el.value.toLowerCase().replace('uk', '').trim() === size
    );

    // Dispatch even if already checked: a color switch resets the card to its first
    // available variant while leaving the old radio checked
    if (input && !input.disabled) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#updateStock(item);
  }

  #updateStock(item) {
    if (!this.lowStock) return;

    const variantId = item.querySelector('.nms-product-card__cart-form input[name="id"]')?.value;
    const qty = this.#getItemData(item).stock[variantId] || 0;
    const show = qty > 0 && qty <= this.lowStock;

    let stock = item.querySelector('[data-clearance-stock]');
    if (!stock && show) {
      stock = this.#createStock();
      const anchor = item.querySelector('[data-ref="priceContainer"]');
      anchor ? anchor.after(stock) : item.querySelector('.nms-product-card__form')?.before(stock);
    }
    if (!stock) return;

    stock.hidden = !show;
    if (!show) return;
    stock.querySelector('[data-stock-text]').textContent = this.stockTemplate.replace('[count]', qty);
    // Fill shows what's sold; the empty tail (8–40% of the bar) is the pairs still left
    const leftShare = Math.round(8 + Math.min(1, qty / this.lowStock) * 32);
    stock.querySelector('[data-stock-fill]').style.width = `${100 - leftShare}%`;
  }

  #createStock() {
    const stock = document.createElement('div');
    stock.className = 'nms-clearance-store__stock';
    stock.dataset.clearanceStock = '';
    stock.innerHTML = `
      <p class="nms-text-label-sm nms-clearance-store__stock-text">
        <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false">
          <path d="M183.89,153.34a57.6,57.6,0,0,1-46.56,46.55A8.75,8.75,0,0,1,136,200a8,8,0,0,1-1.32-15.89c16.57-2.79,30.63-16.85,33.44-33.45a8,8,0,0,1,15.78,2.68ZM216,144a88,88,0,0,1-176,0c0-27.92,11-56.47,32.66-84.85a8,8,0,0,1,11.93-.89l24.12,23.41,22-60.41a8,8,0,0,1,12.63-3.41C165.21,36,216,84.55,216,144Zm-16,0c0-46.09-35.79-85.92-58.21-106.33L119.52,98.74a8,8,0,0,1-13.09,3L80.06,76.16C64.09,99.21,56,122,56,144a72,72,0,0,0,144,0Z"/>
        </svg>
        <span data-stock-text></span>
      </p>
      <span class="nms-clearance-store__stock-track" aria-hidden="true">
        <span class="nms-clearance-store__stock-fill" data-stock-fill></span>
      </span>`;
    return stock;
  }

  #updateViewAll(size) {
    const wrapper = this.element.querySelector('[data-view-all]');
    const link = wrapper?.querySelector('a');
    if (!link) return;

    const url = new URL(wrapper.dataset.baseUrl, window.location.origin);
    url.searchParams.set('filter.v.option.size', `UK ${size}`);
    link.href = `${url.pathname}${url.search}`;
  }

  #getItemData(item) {
    if (!this.#itemData.has(item)) {
      let data = { colors: {}, stock: {} };
      try {
        data = JSON.parse(item.querySelector('[data-clearance-data]')?.textContent || '{}');
        data.colors = data.colors || {};
        data.stock = data.stock || {};
      } catch (error) {
        this.handleError?.(error, 'Clearance store data failed to parse');
      }
      this.#itemData.set(item, data);
    }
    return this.#itemData.get(item);
  }
}

function initNMSClearanceStore() {
  if (!window.NMSBaseClass) {
    console.error('NMS Clearance Store: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  // Product cards must be live first: their change/color handlers do the variant work
  window.initNMSProductCard?.();

  for (const element of document.querySelectorAll('[data-nms-clearance-store]')) {
    if (element.hasAttribute('data-initialized')) continue;
    try {
      const instance = new NMSClearanceStore(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('Failed to initialize NMS Clearance Store:', error, element);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSClearanceStore);
} else {
  initNMSClearanceStore();
}

document.addEventListener('nms:content-updated', initNMSClearanceStore);
document.addEventListener('shopify:section:load', initNMSClearanceStore);

window.initNMSClearanceStore = initNMSClearanceStore;
